import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState, AppDispatch } from "../../../app/store";
import { getWithAuth, postJsonWithAuth, postWithAuth } from "../../../utils/api";
import { openSnackbar } from "../../../features/snackbar/globalSnackbarSlice";
import { DataCollectorRequestType, lambdacontrolState } from "./lambdacontrolTypes";
import { messstelle, datenklasse, messwertLog } from "./../../../utils/apiTypes";
import { logError } from "../../../utils/utils";
import { errorHandler } from "../../../utils/errorHandling";

const emptyDatenklasse: datenklasse = {
  id: 0,
  klasse: "",
  hersteller: "",
  beschreibung: "",
  istSporadisch: false,
};

const initialState: lambdacontrolState = {
  messstellen: [],
  isMessstellenLoading: false,
  selectedMessstellen: [],
  selectedMessstellenGroups: [],
  datenklassen: [],
  isDatenklassenLoading: false,
  selectedDatenklasse: emptyDatenklasse,
  startDatum: new Date(),
  endeDatum: new Date(),
  messwertLogs: [],
  isMesswertLogsLoading: false,
};

const lambdacontrolSlice = createSlice({
  name: "lambdacontrol",
  initialState,
  reducers: {
    setMessstellen: (state, action: PayloadAction<messstelle[]>) => {
      state.messstellen = action.payload;
    },
    setIsMessstellenLoading: (state, action: PayloadAction<boolean>) => {
      state.isMessstellenLoading = action.payload;
    },
    setSelectedMessstellen: (state, action: PayloadAction<messstelle[]>) => {
      state.selectedMessstellen = action.payload;
    },
    setSelectedMessstellenGroups: (state, action: PayloadAction<string[]>) => {
      state.selectedMessstellenGroups = action.payload;
    },
    selectFilteredMessstellenOfGroup: (state, action: PayloadAction<{ groups: Array<string> | null; filter: string }>) => {
      const filter = action.payload.filter.toLowerCase();
      if (action.payload.groups) {
        const grouplessMessstellen = state.selectedMessstellen
          .filter((x) => !action.payload.groups?.includes(x.umspannwerk))
          .filter(
            (x) =>
              state.messstellen.filter((m) => m.umspannwerk == x.umspannwerk).length !=
              state.selectedMessstellen.filter((m) => m.umspannwerk == x.umspannwerk).length
          );
        state.selectedMessstellen = state.messstellen.filter((x) =>
          x.umspannwerk ? action.payload.groups?.includes(x.umspannwerk) : false
        );
        state.selectedMessstellen = state.selectedMessstellen.concat(grouplessMessstellen);
        state.selectedMessstellen = state.selectedMessstellen.filter(
          (x) =>
            x.name.toLowerCase().includes(filter) ||
            x.schaltfeld.toLowerCase().includes(filter) ||
            x.umspannwerk.toLowerCase().includes(filter)
        );
      } else {
        state.selectedMessstellen = state.messstellen.filter(
          (x) =>
            x.name.toLowerCase().includes(filter) ||
            x.schaltfeld.toLowerCase().includes(filter) ||
            x.umspannwerk.toLowerCase().includes(filter)
        );
      }
    },
    setDatenklassen: (state, action: PayloadAction<datenklasse[]>) => {
      state.datenklassen = action.payload;
    },
    setIsDatenklassenLoading: (state, action: PayloadAction<boolean>) => {
      state.isDatenklassenLoading = action.payload;
    },
    setSelectedDatenklasse: (state, action: PayloadAction<datenklasse>) => {
      state.selectedDatenklasse = action.payload;
    },
    setStartDatum: (state, action: PayloadAction<Date>) => {
      state.startDatum = action.payload;
    },
    setEndeDatum: (state, action: PayloadAction<Date>) => {
      state.endeDatum = action.payload;
    },
    setMesswertLogs: (state, action: PayloadAction<messwertLog[]>) => {
      state.messwertLogs = action.payload;
    },
    setIsMesswertLogsLoading: (state, action: PayloadAction<boolean>) => {
      state.isMesswertLogsLoading = action.payload;
    },
  },
});

export const selectMessstellen = (state: RootState): messstelle[] => state.lambdacontrol.messstellen;
export const selectIsMessstellenLoading = (state: RootState): boolean => state.lambdacontrol.isMessstellenLoading;
export const selectSelectedMessstellen = (state: RootState): messstelle[] => state.lambdacontrol.selectedMessstellen;
export const selectSelectedMessstellenGroups = (state: RootState): string[] => state.lambdacontrol.selectedMessstellenGroups;
export const selectDatenklassen = (state: RootState): datenklasse[] => state.lambdacontrol.datenklassen;
export const selectIsDatenklassenLoading = (state: RootState): boolean => state.lambdacontrol.isDatenklassenLoading;
export const selectSelectedDatenklasse = (state: RootState): datenklasse => state.lambdacontrol.selectedDatenklasse;
export const selectStartDatum = (state: RootState): Date => state.lambdacontrol.startDatum;
export const selectEndeDatum = (state: RootState): Date => state.lambdacontrol.endeDatum;
export const selectMesswertLogs = (state: RootState): messwertLog[] => state.lambdacontrol.messwertLogs;
export const selectIsMesswertLogsLoading = (state: RootState): boolean => state.lambdacontrol.isMesswertLogsLoading;

export const {
  setMessstellen,
  setIsMessstellenLoading,
  setSelectedMessstellen,
  setSelectedMessstellenGroups,
  selectFilteredMessstellenOfGroup,
  setDatenklassen,
  setIsDatenklassenLoading,
  setSelectedDatenklasse,
  setStartDatum,
  setEndeDatum,
  setMesswertLogs,
  setIsMesswertLogsLoading,
} = lambdacontrolSlice.actions;

export const getMessstellen =
  () =>
  (dispatch: AppDispatch): void => {
    dispatch(setIsMessstellenLoading(true));
    getWithAuth("MESSSTELLE", {})
      .then((response: messstelle[]) => {
        dispatch(setMessstellen(response.filter((m) => m.jemalsZugeordnet)));
      })
      .catch((error) => {
        logError(error);
        errorHandler(error, dispatch);
      })
      .finally(() => dispatch(setIsMessstellenLoading(false)));
  };

export const getDatenklassen =
  (messstelle: string) =>
  (dispatch: AppDispatch): void => {
    dispatch(setIsDatenklassenLoading(true));
    getWithAuth("DATENKLASSENID", {}, messstelle)
      .then((response: datenklasse[]) => {
        dispatch(setDatenklassen(response));
      })
      .catch((error) => {
        logError(error);
        errorHandler(error, dispatch);
      })
      .finally(() => dispatch(setIsDatenklassenLoading(false)));
  };

export const getMesswertLogs =
  (messstellen: string[], datenklasse: datenklasse) =>
  (dispatch: AppDispatch): void => {
    dispatch(setIsMesswertLogsLoading(true));
    postWithAuth(
      "MESSWERTLOG",
      {
        hersteller: messstellen.length == 1 ? datenklasse.hersteller : "Eberle",
        datenklasse: messstellen.length == 1 ? datenklasse.klasse : "c10m",
        from: new Date(0),
        to: new Date(),
      },
      messstellen.join(",")
    )
      .then((response: messwertLog[]) => {
        dispatch(setMesswertLogs(response));
      })
      .catch((error) => {
        logError(error);
        errorHandler(error, dispatch);
      })
      .finally(() => dispatch(setIsMesswertLogsLoading(false)));
  };

export const startDataCollector =
  (from: Date, to: Date, messstellen: string[], type: DataCollectorRequestType) =>
  (dispatch: AppDispatch): void => {
    postJsonWithAuth(
      "BACKEND",
      {
        from: from,
        to: to,
        dataCollectorRequestType: type,
      },
      {
        Kenngroessen: null,
        Messstellen: messstellen,
      },
      "datacollector"
    )
      .then(() => {
        dispatch(
          openSnackbar({
            message: "Starte DataColletor...",
            severity: "success",
            duration: 6000,
          })
        );
      })
      .catch((error) => {
        logError(error);
        errorHandler(error, dispatch);
      });
  };

export const startIrregularDataChecker =
  (from: Date, to: Date, messstellen: string[]) =>
  (dispatch: AppDispatch): void => {
    postJsonWithAuth(
      "BACKEND",
      {
        from: from,
        to: to,
      },
      {
        Kenngroessen: null,
        Messstellen: messstellen,
      },
      "irregulardatachecker"
    )
      .then(() => {
        dispatch(
          openSnackbar({
            message: "Starte IrregularDataChecker...",
            severity: "success",
            duration: 6000,
          })
        );
      })
      .catch((error) => {
        logError(error);
        errorHandler(error, dispatch);
      });
  };

export const startQuantileCalculator =
  (from: Date, to: Date, messstellen: string[]) =>
  (dispatch: AppDispatch): void => {
    postJsonWithAuth(
      "BACKEND",
      {
        from: from,
        to: to,
      },
      {
        Kenngroessen: null,
        Messstellen: messstellen,
      },
      "quantilecalculator"
    )
      .then(() => {
        dispatch(
          openSnackbar({
            message: "Starte QuantileCalculator...",
            severity: "success",
            duration: 6000,
          })
        );
      })
      .catch((error) => {
        logError(error);
        errorHandler(error, dispatch);
      });
  };

export const startCorrelationCalculator =
  (from: Date, to: Date, messstellen: string[]) =>
  (dispatch: AppDispatch): void => {
    postJsonWithAuth(
      "BACKEND",
      {
        from: from,
        to: to,
      },
      {
        Kenngroessen: null,
        Messstellen: messstellen,
      },
      "correlationcalculator"
    )
      .then(() => {
        dispatch(
          openSnackbar({
            message: "Starte CorrelationCalculator...",
            severity: "success",
            duration: 6000,
          })
        );
      })
      .catch((error) => {
        logError(error);
        errorHandler(error, dispatch);
      });
  };

export const startAnomalyCalculator =
  (from: Date, to: Date, messstellen: string[]) =>
  (dispatch: AppDispatch): void => {
    postJsonWithAuth(
      "BACKEND",
      {
        from: from,
        to: to,
      },
      {
        Kenngroessen: null,
        Messstellen: messstellen,
      },
      "anomalycalculator"
    )
      .then(() => {
        dispatch(
          openSnackbar({
            message: "Starte AnomalyCalculator...",
            severity: "success",
            duration: 6000,
          })
        );
      })
      .catch((error) => {
        logError(error);
        errorHandler(error, dispatch);
      });
  };

export const startTrendsCalculator =
  (from: Date, to: Date, messstellen: string[]) =>
  (dispatch: AppDispatch): void => {
    postJsonWithAuth(
      "BACKEND",
      {
        from: from,
        to: to,
      },
      {
        Kenngroessen: null,
        Messstellen: messstellen,
      },
      "trendscalculator"
    )
      .then(() => {
        dispatch(
          openSnackbar({
            message: "Starte TrendsCalculator...",
            severity: "success",
            duration: 6000,
          })
        );
      })
      .catch((error) => {
        logError(error);
        errorHandler(error, dispatch);
      });
  };

export default lambdacontrolSlice.reducer;
